/**
 * Heatmap application state.
 * Stores data and functions needed to track the heatmap's state.
 */

/** Dependencies -------------------------------------------- */

// External

import Fuse from 'fuse.js'
import { create } from 'zustand'

// Internal

import {
    mapMaxBounds,
    stateBoundaryLevel,
    getPlugin,
    getDevelopmentType,
    getDevelopmentLevel,
    getPreambleAcceptanceCookie,
    normalizeAggregate,
    normalizeDevelopment
} from './dictionary'

/** Constants ----------------------------------------------- */

/**
 * FuseJS default search options.
 */
const defaultFuseOptions = {
    keys             : [ 'title', 'shortDescription', 'description' ],
    threshold        : 0.3,
    distance         : 100,
    findAllMatches   : true,
    includeMatches   : true,
    includeScore     : true,
    location         : 0,
    useExtendedSearch: true
}

/** Main ---------------------------------------------------- */

/**
 * App global state.
 */
export const useAppState = create( ( set, get ) => ( {

    // Global app state

    isLoading     : true,
    setLoaded     : () => set( { isLoading: false } ),
    hasPreamble   : ! getPreambleAcceptanceCookie(),
    setHasPreamble: ( hasPreamble ) => set( { hasPreamble: hasPreamble } ),
    isError       : false,
    setError      : () => set( { isLoading: false, isError: true } ),
    aggregate     : null,
    setAggregate  : ( aggregateId ) => {
        set( { aggregate: getAggregateById( get().aggregatesData, aggregateId ) } )
        syncDevelopmentQuery()
    },
    keywordsFilter         : null,
    filterByKeywords       : ( keywords ) => {
        set( { keywordsFilter: keywords.length ? keywords : null } )
        syncDevelopmentQuery()
    },
    developmentTypeFilter  : null,
    filterByDevelopmentType: ( developmentTypeId ) => {
        set( { developmentTypeFilter: getDevelopmentType( developmentTypeId ) || null })
        syncDevelopmentQuery()
    },
    developmentLevelFilter  : null,
    filterByDevelopmentLevel: ( developmentLevelId ) => {
        set( { developmentLevelFilter: getDevelopmentLevel( developmentLevelId ) || null })
        syncDevelopmentQuery()
    },
    developments  : [],
    development   : null,
    setDevelopment: ( developmentId ) => {

        const development = getDevelopmentById( get().developmentsData, developmentId )
        set( { development: development } )

        if ( development ) {
            get().setAggregate( development.stateAggregate.id )
        }
        else {
            syncDevelopmentQuery()
        }
    },

    // Heatmap data

    stateBounds     : {},
    aggregatesData  : [],
    developmentsData: [],

    // Mapbox

    map         : null,
    setMap      : ( map ) => set( { map: map } ),
    latitude    : 0,
    longitude   : 0,
    zoom        : 0,
    setViewState: ( viewState ) => set( { latitude: viewState.latitude, longitude: viewState.longitude, zoom: viewState.zoom } ),
    zoomIn      : () => get().map.easeTo( { zoom: get().zoom + getPlugin().zoomInterval } ),
    zoomOut     : () => get().map.easeTo( { zoom: get().zoom - getPlugin().zoomInterval } ),
    maxBounds   : mapMaxBounds,
    setMaxBounds: ( maxBounds ) => set( { maxBounds: maxBounds } ),

    // Initialization

    initialize: async () => {

        const plugin = getPlugin()

        // First, get heatmap data

        let heatmapDataResponse

        try {
            heatmapDataResponse = await fetch( plugin.dataUrl )
        }
        catch ( error ) {
            get().setError()
            return
        }

        if ( ! heatmapDataResponse.ok ) {
            get().setError()
            return
        }

        // Then, we map the aggregates to each developments

        const heatmapData = await heatmapDataResponse.json()

        for ( const aggregateData of heatmapData.aggregates ) {

            for ( const developmentData of heatmapData.developments ) {
                
                if ( developmentData.stateAggregate === aggregateData.id ) {
                    developmentData.stateAggregate = aggregateData
                }
            }
        }

        // Then, we get and format state bounds data

        const stateBoundsDataResponse = await fetch( plugin.stateBoundsGeoJsonUrl )

        if ( ! stateBoundsDataResponse.ok ) {
            get().setError()
            return
        }

        const stateBoundsData = await stateBoundsDataResponse.json()

        for ( let aggregate of heatmapData.aggregates ) {

            const aggregateStateBound = stateBoundsData.features.find(
                ( stateBound ) => aggregate.boundaryLevel = stateBoundaryLevel && stateBound.id === aggregate.boundaryId
            )

            aggregateStateBound.properties.aggregateId = aggregate.id
            aggregateStateBound.properties.aggregateDevelopmentLevelNumericValue = getDevelopmentLevel( aggregate.developmentLevel ).numericValue
        }

        set( {
            stateBoundsData : stateBoundsData,
            aggregatesData  : heatmapData.aggregates,
            developmentsData: heatmapData.developments
        } )

        syncDevelopmentQuery()
    }
} ) )

/** State methods ------------------------------------------- */

/**
 * Computes developments from the state's current filter and sort values.
 */
function syncDevelopmentQuery() {

    const appState = useAppState.getState()

    const query = {
        filter: {
            aggregate       : appState.aggregate,
            keywords        : appState.keywordsFilter,
            developmentType : appState.developmentTypeFilter,
            developmentLevel: appState.developmentLevelFilter,
            development     : appState.development
        },
        sort  : [ 'created', 'desc' ]
    }
    
    let syncedDevelopments = appState.developmentsData

    for ( let [ fieldName, fieldValue ] of Object.entries( query.filter ) ) {
        syncedDevelopments = filterDevelopments( syncedDevelopments, fieldName, fieldValue )
    }

    const sortedDevelopments = sortDevelopments( syncedDevelopments, null, 'desc' )
    useAppState.setState( { developments: sortedDevelopments } )
}

/** Utils --------------------------------------------------- */

/**
 * Gets an aggregate by ID.
 */
function getAggregateById( aggregates, aggregateId ) {
    const foundAggregate = aggregates.find( ( aggregate ) => aggregate.id === aggregateId )
    return foundAggregate ? normalizeAggregate( foundAggregate ) : null
}

/**
 * Gets development by ID.
 */
function getDevelopmentById( developments, developmentId ) {
    const foundDevelopment = developments.find( ( development ) => development.id === developmentId )
    return foundDevelopment ? normalizeDevelopment( foundDevelopment ) : null
}

/**
 * Filters developments via a predetermined method of doing so.
 */
function filterDevelopments( developments, method = null, ...methodArgs ) {

    switch ( method ) {
        case 'aggregate':
            method = filterDevelopmentsByAggregate
            break
        case 'keywords':
            method = filterDevelopmentsByKeywords
            methodArgs.push( developments )
            break
        case 'developmentType':
            method = filterDevelopmentsByDevelopmentType
            break
        case 'developmentLevel':
            method = filterDevelopmentsByDevelopmentLevel
            break
        case 'development':
            method = filterDevelopmentsByDevelopment
            break
        default:
            method = false
    }

    const filterCallback = method ? method( ...methodArgs ) : false
    return filterCallback ? developments.filter( method( ...methodArgs ) ) : developments
}

/**
 * Filters developments by aggregate.
 */
function filterDevelopmentsByAggregate( aggregate ) {
    return aggregate?.id
        ? ( development ) => development.stateAggregate.id === aggregate.id
        : false
}

/**
 * Searches developments by keywords.
 */
function filterDevelopmentsByKeywords( keywords, developments ) {

    if ( keywords === null || ! keywords.length ) {
        return false
    }

    // Construct the query based on keywords and their associated terms
    // We flatten these out, then add them into a giant OR conditional

    const flattenedKeywords = []

    for ( let keyword of keywords ) {

        if ( typeof keyword === 'string' || keyword instanceof String ) {
            flattenedKeywords.push( keyword )
        }
        else {
            const derivedKeywords = keyword.terms.length ? keyword.terms : [keyword.label]
            flattenedKeywords.push( ...derivedKeywords )
        }
    }

    const query = flattenedKeywords.join( '|' )

    // Then we query using Fuse
    // We keep the original objects around here in case we need to factor in scores for sorting, etc. later on

    const fuse = new Fuse( developments, defaultFuseOptions )
    const fuseResults = fuse.search( query )

    const matchedDevelopmentIds= fuseResults.map( ( fuseResult ) => fuseResult.item.id )

    return ( development ) => matchedDevelopmentIds.includes( development.id )
}

/**
 * Filters developments by development type.
 */
function filterDevelopmentsByDevelopmentType( developmentType ) {
    return developmentType?.id
        ? ( development ) => development.developmentType === developmentType.id
        : false
}

/**
 * Filters developments by development level.
 */
function filterDevelopmentsByDevelopmentLevel( developmentLevel ) {
    return developmentLevel?.id
        ? ( development ) => development.developmentLevel === developmentLevel.id
        : false
}

/**
 * Filters developments by development (uses ID).
 */
function filterDevelopmentsByDevelopment( development ) {
    return development?.id
        ? ( developmentData ) => developmentData.id === development.id
        : false
}

/**
 * Sorts developments via a predefined method of doing so.
 * Currently only supports sorting by creation date.
 */
function sortDevelopments( developments, method = null, order = 'asc' ) {

    switch ( method ) {
        default:
            method = sortDevelopmentsByCreated
    }

    const sortedDevelopments = developments.toSorted( method )
    return order === 'asc' ? sortedDevelopments : sortedDevelopments.reverse()
}

/**
 * Sorts developments by created date, falling back to updated date and title.
 * Always in ASC order.
 */
function sortDevelopmentsByCreated( developmentA, developmentB ) {

    const developmentACreated = developmentA.created
    const developmentBCreated = developmentB.created

    if ( developmentACreated > developmentBCreated ) {
        return 1
    }
    if ( developmentACreated < developmentBCreated ) {
        return -1
    }

    const developmentAUpdated = developmentA.updated
    const developmentBUpdated = developmentB.updated

    if ( developmentAUpdated > developmentBUpdated ) {
        return 1
    }
    if ( developmentAUpdated < developmentBUpdated ) {
        return -1
    }

    const developmentATitle= developmentA.title
    const developmentBTitle= developmentB.title

    return developmentATitle.localeCompare( developmentBTitle )
}


